<template>
  <div class="quick-filter-panel">
    <!-- Filter Header with Active Count -->
    <div
      v-if="showHeader"
      class="filter-header d-flex align-center mb-3"
    >
      <font-awesome-icon
        :icon="['fas', 'filter']"
        class="primary--text mr-2"
        size="sm"
      />
      <span class="subtitle-2 font-weight-medium">{{ title }}</span>

      <v-spacer />

      <template v-if="hasActiveFilters">
        <v-chip
          small
          color="primary"
          text-color="white"
          class="mr-2"
        >
          {{ activeFilterCount }} active
        </v-chip>
        <v-btn
          small
          text
          color="primary"
          class="text-none"
          @click="clearAllFilters"
        >
          Clear All
        </v-btn>
      </template>
    </div>

    <!-- Filter Fields -->
    <div class="filter-fields">
      <div
        v-for="(field, index) in fields"
        :key="field.key"
        class="filter-field"
        :class="{ 'mb-3': index < fields.length - 1 }"
      >
        <!-- Field Label (optional, shown above select) -->
        <div
          v-if="showLabels"
          class="filter-label mb-1"
        >
          <font-awesome-icon
            v-if="field.icon"
            :icon="field.icon"
            class="mr-2"
            :class="getFieldValue(field.key) ? 'primary--text' : 'grey--text'"
            size="sm"
          />
          <span
            class="text-caption font-weight-medium"
            :class="{ 'primary--text': getFieldValue(field.key) }"
          >
            {{ field.label }}
          </span>
        </div>

        <!-- Text Input Field -->
        <v-text-field
          v-if="field.type === 'text'"
          :value="getFieldValue(field.key) || ''"
          :placeholder="field.placeholder || `Search ${field.label}...`"
          :loading="field.loading"
          dense
          outlined
          hide-details
          @input="updateFilter(field.key, $event)"
        >
          <template
            v-if="field.icon && !showLabels"
            v-slot:prepend-inner
          >
            <font-awesome-icon
              :icon="field.icon"
              :style="{ color: getFieldValue(field.key) ? 'var(--v-primary-base)' : '#bdbdbd', verticalAlign: '0' }"
              size="sm"
            />
          </template>

          <!-- Clear button with Font Awesome icon -->
          <template v-slot:append-inner>
            <v-btn
              v-if="getFieldValue(field.key)"
              icon
              x-small
              class="ma-0"
              :aria-label="`Clear ${field.label}`"
              @click.stop="updateFilter(field.key, '')"
            >
              <font-awesome-icon
                :icon="['fas', 'xmark']"
                size="sm"
                :style="{ color: '#9e9e9e' }"
              />
            </v-btn>
          </template>
        </v-text-field>

        <!-- Number Input Field -->
        <v-text-field
          v-else-if="field.type === 'number'"
          :value="getFieldValue(field.key)"
          :placeholder="field.placeholder || field.label"
          type="number"
          :min="field.min"
          :max="field.max"
          :step="field.step || 1"
          dense
          outlined
          hide-details
          @input="updateFilter(field.key, $event ? Number($event) : null)"
        >
          <template
            v-if="field.icon && !showLabels"
            v-slot:prepend-inner
          >
            <font-awesome-icon
              :icon="field.icon"
              :style="{ color: getFieldValue(field.key) != null ? 'var(--v-primary-base)' : '#bdbdbd', verticalAlign: '0' }"
              size="sm"
            />
          </template>

          <!-- Clear button with Font Awesome icon -->
          <template v-slot:append-inner>
            <v-btn
              v-if="getFieldValue(field.key) != null"
              icon
              x-small
              class="ma-0"
              :aria-label="`Clear ${field.label}`"
              @click.stop="updateFilter(field.key, null)"
            >
              <font-awesome-icon
                :icon="['fas', 'xmark']"
                :style="{ color: '#9e9e9e' }"
                size="sm"
              />
            </v-btn>
          </template>
        </v-text-field>

        <!-- Boolean Field (Switch) -->
        <div
          v-else-if="field.type === 'boolean'"
          class="boolean-field d-flex align-center pa-2"
          :class="{ 'boolean-field--active': getFieldValue(field.key) }"
        >
          <font-awesome-icon
            v-if="field.icon && !showLabels"
            :icon="field.icon"
            :style="{ color: getFieldValue(field.key) ? 'var(--v-primary-base)' : '#bdbdbd' }"
            size="sm"
            class="mr-2"
          />
          <span class="text-body-2">{{ field.label }}</span>
          <v-spacer />
          <v-switch
            :input-value="getFieldValue(field.key) || false"
            hide-details
            dense
            class="mt-0 pt-0"
            @change="updateFilter(field.key, $event)"
          />
        </div>

        <!-- Date Field -->
        <v-menu
          v-else-if="field.type === 'date'"
          :close-on-content-click="false"
          transition="scale-transition"
          offset-y
          min-width="290px"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              :value="getFieldValue(field.key)"
              :placeholder="field.placeholder || field.label"
              readonly
              dense
              outlined
              hide-details
              v-bind="attrs"
              v-on="on"
            >
              <template
                v-if="field.icon && !showLabels"
                v-slot:prepend-inner
              >
                <font-awesome-icon
                  :icon="field.icon || ['fas', 'calendar']"
                  :style="{ color: getFieldValue(field.key) ? 'var(--v-primary-base)' : '#bdbdbd', verticalAlign: '0' }"
                  size="sm"
                />
              </template>

              <!-- Clear button with Font Awesome icon -->
              <template v-slot:append-inner>
                <v-btn
                  v-if="getFieldValue(field.key)"
                  icon
                  x-small
                  class="ma-0"
                  :aria-label="`Clear ${field.label}`"
                  @click.stop="updateFilter(field.key, null)"
                >
                  <font-awesome-icon
                    :icon="['fas', 'xmark']"
                    :style="{ color: '#9e9e9e' }"
                    size="sm"
                  />
                </v-btn>
              </template>
            </v-text-field>
          </template>
          <v-date-picker
            :value="getFieldValue(field.key)"
            no-title
            scrollable
            @input="updateFilter(field.key, $event)"
          />
        </v-menu>

        <!-- Multi-Select Field -->
        <v-autocomplete
          v-else-if="field.multiple || field.type === 'multiselect'"
          :value="getFieldValue(field.key) || []"
          :items="field.items || []"
          :placeholder="field.placeholder || `All ${field.label}`"
          :loading="field.loading"
          chips
          small-chips
          deletable-chips
          multiple
          dense
          outlined
          hide-details
          :item-text="field.itemText || 'text'"
          :item-value="field.itemValue || 'value'"
          :item-disabled="field.itemDisabled || 'disabled'"
          @input="updateFilter(field.key, $event)"
        >
          <template
            v-if="field.icon && !showLabels"
            v-slot:prepend-inner
          >
            <font-awesome-icon
              :icon="field.icon"
              :style="{ color: getFieldValue(field.key)?.length ? 'var(--v-primary-base)' : '#bdbdbd', verticalAlign: '0' }"
              size="sm"
            />
          </template>

          <!-- Clear button with Font Awesome icon -->
          <template v-slot:append-inner>
            <v-btn
              v-if="getFieldValue(field.key)?.length"
              icon
              x-small
              class="ma-0"
              :aria-label="`Clear ${field.label}`"
              @click.stop="updateFilter(field.key, [])"
            >
              <font-awesome-icon
                :icon="['fas', 'xmark']"
                :style="{ color: '#9e9e9e' }"
                size="sm"
              />
            </v-btn>
          </template>

          <!-- Custom item slot with Font Awesome checkboxes -->
          <template v-slot:item="{ item, on, attrs }">
            <v-list-item
              v-bind="attrs"
              :disabled="isItemDisabled(item, field)"
              v-on="on"
            >
              <v-list-item-action class="mr-2">
                <font-awesome-icon
                  :icon="attrs.inputValue ? ['fas', 'square-check'] : ['far', 'square']"
                  :class="[attrs.inputValue ? 'primary--text' : 'grey--text', { 'text--disabled': isItemDisabled(item, field) }]"
                />
              </v-list-item-action>
              <v-list-item-content>
                <v-list-item-title :class="{ 'text--disabled': isItemDisabled(item, field) }">
                  {{ getItemText(item, field) }}
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </template>
        </v-autocomplete>

        <!-- Single-Select Field (default) -->
        <v-select
          v-else
          :value="getFieldValue(field.key)"
          :items="field.items || []"
          :placeholder="field.placeholder || `All ${field.label}`"
          :loading="field.loading"
          :color="getFieldValue(field.key) ? 'primary' : undefined"
          dense
          outlined
          hide-details
          :item-text="field.itemText || 'text'"
          :item-value="field.itemValue || 'value'"
          @input="updateFilter(field.key, $event)"
        >
          <template v-slot:label>
            <span :class="{ 'primary--text font-weight-bold': getFieldValue(field.key) }">
              {{ field.label }}
            </span>
          </template>
          <template
            v-if="field.icon && !showLabels"
            v-slot:prepend-inner
          >
            <font-awesome-icon
              :icon="field.icon"
              :style="{ color: getFieldValue(field.key) ? 'var(--v-primary-base)' : '#bdbdbd', verticalAlign: '0' }"
              size="sm"
            />
          </template>

          <!-- Clear button with Font Awesome icon -->
          <template v-slot:append-inner>
            <v-btn
              v-if="getFieldValue(field.key)"
              icon
              x-small
              class="ma-0"
              :aria-label="`Clear ${field.label}`"
              @click.stop="updateFilter(field.key, null)"
            >
              <font-awesome-icon
                :icon="['fas', 'xmark']"
                :style="{ color: '#9e9e9e' }"
                size="sm"
              />
            </v-btn>
          </template>
        </v-select>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * QuickFilterPanel Component
 *
 * A unified, reusable filter panel component for consistent filtering UI across views.
 * Supports both single-select and multi-select fields with icons and active state styling.
 *
 * Usage:
 * <quick-filter-panel
 *   :fields="filterFields"
 *   :values="filterValues"
 *   title="Filter Projects"
 *   @update="handleFilterUpdate"
 *   @clear="handleClearFilters"
 * />
 *
 * Field configuration:
 * {
 *   key: 'status',           // Unique field identifier
 *   label: 'Status',         // Display label
 *   icon: ['fas', 'check'],  // FontAwesome icon (optional)
 *   items: [...],            // Options array
 *   itemText: 'text',        // Item text property (default: 'text')
 *   itemValue: 'value',      // Item value property (default: 'value')
 *   multiple: false,         // Multi-select mode (default: false)
 *   placeholder: 'All...',   // Placeholder text (optional)
 *   loading: false,          // Show loading state (optional)
 * }
 */
export default {
  name: 'QuickFilterPanel',

  props: {
    /**
     * Array of filter field configurations
     */
    fields: {
      type: Array,
      required: true,
      validator: (fields) => fields.every((f) => f.key && f.label),
    },

    /**
     * Current filter values object { fieldKey: value }
     */
    values: {
      type: Object,
      default: () => ({}),
    },

    /**
     * Panel title (shown in header)
     */
    title: {
      type: String,
      default: 'Filters',
    },

    /**
     * Show header with title and clear button
     */
    showHeader: {
      type: Boolean,
      default: true,
    },

    /**
     * Show labels above each field (alternative to inline prepend-inner icons)
     */
    showLabels: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    /**
     * Count of fields with active filter values
     */
    activeFilterCount() {
      return this.fields.filter((field) => {
        const value = this.getFieldValue(field.key)
        if (Array.isArray(value)) {
          return value.length > 0
        }
        return value !== null && value !== undefined && value !== ''
      }).length
    },

    /**
     * Whether any filters are active
     */
    hasActiveFilters() {
      return this.activeFilterCount > 0
    },
  },

  methods: {
    /**
     * Get filter value for a field
     */
    getFieldValue(key) {
      return this.values[key]
    },

    /**
     * Get display text from an item based on field configuration
     * @param {*} item - The item (string or object)
     * @param {Object} field - The field configuration
     * @returns {string} - Display text for the item
     */
    getItemText(item, field) {
      if (typeof item === 'string') {
        return item
      }
      const textKey = field.itemText || 'text'
      return item[textKey] || item?.value || String(item)
    },

    isItemDisabled(item, field) {
      const itemDisabled = field?.itemDisabled
      if (typeof itemDisabled === 'function') {
        return !!itemDisabled(item)
      }
      if (typeof itemDisabled === 'string') {
        return !!item?.[itemDisabled]
      }
      return !!item?.disabled
    },

    /**
     * Update a single filter value
     */
    updateFilter(key, value) {
      const newValues = { ...this.values, [key]: value }
      this.$emit('update', newValues)
      this.$emit(`update:${key}`, value)
    },

    /**
     * Clear all filter values
     */
    clearAllFilters() {
      const clearedValues = {}
      this.fields.forEach((field) => {
        clearedValues[field.key] = field.multiple ? [] : null
      })
      this.$emit('update', clearedValues)
      this.$emit('clear')
    },

    /**
     * Get current filter values (for parent to call via ref)
     */
    getFilters() {
      return { ...this.values }
    },

    /**
     * Reset all filters (for parent to call via ref)
     */
    resetFilters() {
      this.clearAllFilters()
    },
  },
}
</script>

<style scoped lang="scss">
.quick-filter-panel {
  .filter-header {
    min-height: 32px;
  }

  .filter-label {
    display: flex;
    align-items: center;
    font-size: 0.75rem;
    line-height: 1.25;
  }

  .filter-field {
    // Ensure consistent field height
    ::v-deep .v-input {
      .v-input__slot {
        min-height: 40px;
      }

      .v-input__prepend-inner {
        align-self: stretch;
        display: flex;
        align-items: center;
        margin-top: 0 !important;
      }

      .v-input__prepend-inner .svg-inline--fa {
        line-height: 1;
        margin-top: 0;
      }
    }

    // Chip styling for multi-select
    ::v-deep .v-chip {
      margin: 2px 4px 2px 0;
    }
  }

  // Boolean field styling
  .boolean-field {
    border: 1px solid rgba(0, 0, 0, 0.24);
    border-radius: 4px;
    min-height: 40px;
    transition: border-color 0.2s ease;
  }

  .boolean-field--active {
    border-color: var(--v-primary-base);
    background-color: rgba(var(--v-primary-base-rgb), 0.04);
  }
}

// Dark mode support
.theme--dark {
  .quick-filter-panel {
    .filter-label {
      .grey--text {
        color: rgba(255, 255, 255, 0.5) !important;
      }
    }

    .boolean-field {
      border-color: rgba(255, 255, 255, 0.24);
    }

    .boolean-field--active {
      background-color: rgba(var(--v-primary-base-rgb), 0.08);
    }
  }
}
</style>
